import { createContext, useContext, useReducer, useCallback, ReactNode, useRef } from 'react';
import { Comment, CommentState, CreateCommentData, UpdateCommentData } from '../types/comment';
import { commentService } from '../services/comments';

interface CommentContextType extends CommentState {
  loadComments: (sectionId: string) => Promise<void>;
  createComment: (data: CreateCommentData) => Promise<void>;
  updateComment: (data: UpdateCommentData) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  clearError: () => void;
  getCommentsForSection: (sectionId: string) => Comment[];
  clearCache: () => void;
}

const CommentContext = createContext<CommentContextType | undefined>(undefined);

type CommentAction =
  | { type: 'LOAD_START'; payload: string }
  | { type: 'LOAD_SUCCESS'; payload: Comment[] }
  | { type: 'LOAD_FAILURE'; payload: string }
  | { type: 'CREATE_SUCCESS'; payload: Comment }
  | { type: 'UPDATE_SUCCESS'; payload: Comment }
  | { type: 'DELETE_SUCCESS'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_CURRENT_SECTION'; payload: string };

interface ExtendedCommentState extends CommentState {
  currentSectionId: string | null;
  allComments: Comment[]; // Store all comments across sections
}

const initialState: ExtendedCommentState = {
  comments: [], // Comments for current section
  allComments: [], // All comments across sections
  currentSectionId: null,
  isLoading: false,
  error: null,
};

function commentReducer(state: ExtendedCommentState, action: CommentAction): ExtendedCommentState {
  switch (action.type) {
    case 'LOAD_START':
      return {
        ...state,
        isLoading: true,
        error: null,
        currentSectionId: action.payload,
      };
    case 'LOAD_SUCCESS':
      // Merge new comments with existing ones, avoiding duplicates
      const existingCommentIds = new Set(state.allComments.map(c => c.id));
      const newComments = action.payload.filter(c => !existingCommentIds.has(c.id));
      const updatedAllComments = [...state.allComments, ...newComments];
      
      return {
        ...state,
        comments: action.payload, // Current section comments
        allComments: updatedAllComments, // All comments
        isLoading: false,
        error: null,
      };
    case 'LOAD_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        comments: [...state.comments, action.payload],
        allComments: [...state.allComments, action.payload],
        error: null,
      };
    case 'UPDATE_SUCCESS':
      return {
        ...state,
        comments: state.comments.map(comment =>
          comment.id === action.payload.id ? action.payload : comment
        ),
        allComments: state.allComments.map(comment =>
          comment.id === action.payload.id ? action.payload : comment
        ),
        error: null,
      };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        comments: state.comments.filter(comment => comment.id !== action.payload),
        allComments: state.allComments.filter(comment => comment.id !== action.payload),
        error: null,
      };
    case 'SET_CURRENT_SECTION':
      // Filter comments for the current section from allComments
      const sectionComments = state.allComments.filter(comment => 
        comment.blockId.startsWith(action.payload + '-block-')
      );
      return {
        ...state,
        currentSectionId: action.payload,
        comments: sectionComments,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

interface CommentProviderProps {
  children: ReactNode;
}

export function CommentProvider({ children }: CommentProviderProps) {
  const [state, dispatch] = useReducer(commentReducer, initialState);
  const loadedSectionsRef = useRef<Set<string>>(new Set());

  const loadComments = useCallback(async (sectionId: string) => {
    // console.log('💬 CommentContext: Loading comments for section:', sectionId);
    
    // If we've already loaded this section and have comments, just switch to it
    if (loadedSectionsRef.current.has(sectionId)) {
      // console.log('💬 CommentContext: Section already loaded, switching to cached comments');
      dispatch({ type: 'SET_CURRENT_SECTION', payload: sectionId });
      return;
    }

    dispatch({ type: 'LOAD_START', payload: sectionId });
    try {
      // Use 'own' scope to ensure users only see their own comments in reading UI
      // This prevents admins from seeing all comments in the reading interface
      const comments = await commentService.getCommentsBySection(sectionId, 'own');
      // console.log('💬 CommentContext: Loaded', comments.length, 'own comments for section:', sectionId);
      
      // Mark this section as loaded
      loadedSectionsRef.current.add(sectionId);
      
      dispatch({ type: 'LOAD_SUCCESS', payload: comments });
    } catch (error) {
      console.error('💬 CommentContext: Failed to load comments:', error);
      dispatch({ type: 'LOAD_FAILURE', payload: error instanceof Error ? error.message : 'Failed to load comments' });
    }
  }, []);

  const createComment = useCallback(async (data: CreateCommentData) => {
    // console.log('💬 CommentContext: Creating comment:', data);
    try {
      const comment = await commentService.createComment(data);
      // console.log('💬 CommentContext: Comment created successfully:', comment);
      dispatch({ type: 'CREATE_SUCCESS', payload: comment });
    } catch (error) {
      console.error('💬 CommentContext: Failed to create comment:', error);
      dispatch({ type: 'LOAD_FAILURE', payload: error instanceof Error ? error.message : 'Failed to create comment' });
    }
  }, []);

  const updateComment = useCallback(async (data: UpdateCommentData) => {
    // console.log('💬 CommentContext: Updating comment:', data);
    try {
      const comment = await commentService.updateComment(data);
      // console.log('💬 CommentContext: Comment updated successfully:', comment);
      dispatch({ type: 'UPDATE_SUCCESS', payload: comment });
    } catch (error) {
      console.error('💬 CommentContext: Failed to update comment:', error);
      dispatch({ type: 'LOAD_FAILURE', payload: error instanceof Error ? error.message : 'Failed to update comment' });
    }
  }, []);

  const deleteComment = useCallback(async (commentId: string) => {
    // console.log('💬 CommentContext: Deleting comment:', commentId);
    try {
      await commentService.deleteComment(commentId);
      // console.log('💬 CommentContext: Comment deleted successfully');
      dispatch({ type: 'DELETE_SUCCESS', payload: commentId });
    } catch (error) {
      console.error('💬 CommentContext: Failed to delete comment:', error);
      dispatch({ type: 'LOAD_FAILURE', payload: error instanceof Error ? error.message : 'Failed to delete comment' });
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const getCommentsForSection = useCallback((sectionId: string): Comment[] => {
    // Return comments that belong to the specified section
    return state.allComments.filter(comment => 
      comment.blockId.startsWith(sectionId + '-block-')
    );
  }, [state.allComments]);

  const clearCache = useCallback(() => {
    // console.log('💬 CommentContext: Clearing cache');
    loadedSectionsRef.current.clear();
  }, []);

  const value: CommentContextType = {
    comments: state.comments,
    isLoading: state.isLoading,
    error: state.error,
    loadComments,
    createComment,
    updateComment,
    deleteComment,
    clearError,
    getCommentsForSection,
    clearCache,
  };

  return (
    <CommentContext.Provider value={value}>
      {children}
    </CommentContext.Provider>
  );
}

export function useComments() {
  const context = useContext(CommentContext);
  if (context === undefined) {
    throw new Error('useComments must be used within a CommentProvider');
  }
  return context;
}
